import { useTranslation } from "react-i18next";
import { useState } from "react";
import type { FacilityPermissionLevel } from "@arc/contracts";
import { FACILITY_PERMISSION_LEVELS } from "@arc/contracts";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Disclosure } from "@/components/ui/disclosure";
import { Switch } from "@/components/ui/switch";
import { PERMISSION_LEVEL_META, PERMISSION_LEVELS_DESC, permissionRank } from "@/components/permission-level";
import { cn } from "@/lib/utils";

/**
 * The five-level ladder (ADR-030 §3.4), top-down 5 → 1 as the legacy renders it. The switches show the MONOTONIC ladder
 * — the current level and everything below on. Live (Phase B): exactly two switches can act, as in the legacy — the
 * CURRENT level (turning it off drops the role one level) and the NEXT level up (turning it on raises the role to it);
 * everything else is inert. Lowering asks first, with OUR semantics (the engine caps at read: grants above the new
 * level act as the new level, nothing is deleted — not the legacy's "will be revoked"). Admin (5) is a SuperAdmin's to
 * give. Each row's explainer (the legacy "?" dialog) is an inline disclosure.
 */
export function HpfpLadder({
  hpfp,
  callerIsSuper,
  canEdit = false,
  isPending = false,
  onChange,
}: {
  hpfp: FacilityPermissionLevel | null;
  callerIsSuper: boolean;
  /** The role is editable by this caller (server-computed) — Phase A callers pass nothing and get the read-only ladder. */
  canEdit?: boolean;
  isPending?: boolean;
  onChange?: (level: FacilityPermissionLevel) => void;
}) {
  const { t } = useTranslation();
  const current = permissionRank(hpfp);
  const [confirmDrop, setConfirmDrop] = useState<FacilityPermissionLevel | null>(null);
  const levelAt = (rank: number): FacilityPermissionLevel | null => FACILITY_PERMISSION_LEVELS[rank - 1] ?? null;
  return (
    <>
      <ol aria-label={t("roles.permissionLevels")} className="m-0 flex list-none flex-col gap-1.5 p-0">
        {PERMISSION_LEVELS_DESC.map((level) => {
          const meta = PERMISSION_LEVEL_META[level];
          const on = meta.rank <= current;
          const isCurrent = meta.rank === current;
          const isNextUp = meta.rank === current + 1;
          const superGate = level === "access_manage" && !callerIsSuper;
          const actionable = canEdit && !isPending && !!onChange && (isCurrent || isNextUp) && !superGate;
          return (
            <li
              key={level}
              className={cn(
                "flex items-start gap-3 rounded-lg border px-3 py-2",
                on ? "border-primary/40 bg-muted/40" : "border-border text-muted-foreground",
                isCurrent && "ring-primary ring-1",
              )}
            >
              <span className="w-14 shrink-0 pt-0.5 text-xs font-medium">{t("roles.levelRank", { rank: meta.rank })}</span>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex items-center gap-2 font-medium">
                  <meta.Icon aria-hidden="true" className="size-4 shrink-0" />
                  {t(meta.labelKey)}
                  {superGate ? <span className="text-muted-foreground text-xs font-normal">{t("roles.superAdminOnly")}</span> : null}
                </span>
                <span className="text-xs">{t(meta.shortKey)}</span>
                <Disclosure title={<span className="text-xs">{t("roles.whatCanDo", { label: t(meta.labelKey) })}</span>} className="mt-1">
                  <ul className="m-0 list-disc pl-4 text-xs">
                    {meta.descriptionKeys.map((d) => (
                      <li key={d}>{t(d)}</li>
                    ))}
                  </ul>
                </Disclosure>
              </div>
              <Switch
                isSelected={on}
                isDisabled={!actionable}
                aria-label={t("roles.levelRankLabel", { rank: meta.rank, label: t(meta.labelKey) })}
                onChange={(selected) => {
                  if (!onChange) return;
                  if (selected && isNextUp) onChange(level); // raise to this level
                  else if (!selected && isCurrent) {
                    // Drop one level. Level 1 → no level is never offered: the engine reads NULL as uncapped.
                    const below = levelAt(meta.rank - 1);
                    if (below) setConfirmDrop(below);
                  }
                }}
              />
            </li>
          );
        })}
      </ol>
      <ConfirmDialog
        isOpen={confirmDrop !== null}
        title={t("roles.lowerTheHighestLevel")}
        message={
          confirmDrop
            ? t("roles.levelBecomes", { label: t(PERMISSION_LEVEL_META[confirmDrop].labelKey) })
            : ""
        }
        confirmLabel={t("roles.lowerLevelButton")}
        isPending={isPending}
        onConfirm={() => {
          if (confirmDrop && onChange) onChange(confirmDrop);
          setConfirmDrop(null);
        }}
        onCancel={() => setConfirmDrop(null)}
      />
    </>
  );
}
